import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { userIsPro } from '@/lib/tier';
import { Button } from '@/components/ui/button';
import { Sparkles, X, ArrowRight } from 'lucide-react';

const DISMISS_KEY = 'kiteiq_upgrade_nudge_dismissed';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // re-surface after 3 days

const recentlyDismissed = () => {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return ts && (Date.now() - ts) < SNOOZE_MS;
};

/**
 * Friendly, dismissible in-app nudge shown to FREE riders once they've used up
 * their welcome credits — invites them to go Pro. Snoozes for 3 days on dismiss.
 */
export const UpgradeNudge = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(recentlyDismissed());

  const isPro = userIsPro(user);
  const outOfCredits = Number(user?.credits ?? 0) <= 0;
  if (!user || isPro || !outOfCredits || dismissed) return null;

  const close = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      className="relative mt-6 flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-[#00B4D8]/30 bg-[#00B4D8]/8 p-5 pr-12"
      data-testid="upgrade-nudge"
    >
      <div className="w-11 h-11 rounded-xl bg-[#00B4D8]/15 flex items-center justify-center shrink-0">
        <Sparkles className="w-5 h-5 text-[#00B4D8]" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-white">{t('dashboard.upgradeTipTitle')}</h3>
        <p className="text-sm text-muted-foreground mt-0.5">{t('dashboard.upgradeTipDesc')}</p>
      </div>
      <Button
        onClick={() => navigate('/pricing')}
        data-testid="upgrade-nudge-cta"
        className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl shrink-0"
      >
        {t('dashboard.upgradeTipCta')} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
      <button
        type="button"
        onClick={close}
        aria-label={t('dashboard.upgradeTipDismiss')}
        data-testid="upgrade-nudge-dismiss"
        className="absolute top-3 right-3 w-7 h-7 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:text-white hover:bg-white/10 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
